import type { CompanyMetadata } from 'common/api/api-types';
import type * as React from 'react';

import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { styled, type WebTarget } from 'styled-components';

import { Checkmark } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';

import Skeleton from 'common/components/Skeleton';
import { MIDNIGHT_BLUE_TOKEN } from 'common/constants';
import {
  useMatchCourierDetails,
  useMatchGeoLocationHistory,
  useMatchLiveDeliveries,
} from 'common/hooks/useAppRoutes';
import { useAuthState } from 'common/utils/authenticatorUtils';

import useCompaniesByUserId from './hooks/useCompaniesByUserId';
import useCurrentCompany from './hooks/useCurrentCompany';

const StyledMenuItemGroup = styled(MenuItemGroup)`
  // FIXME: A hacky workaround to customize the style of the title.
  > div:first-of-type {
    ${typographyCss.Overline3()};
    color: ${color.fixedWhite};
  }
  display: flex;
  flex-direction: column-reverse;
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
`;

const StyledMenuActionItemCompany = styled<{ $active?: boolean } & WebTarget>(MenuActionItem)`
  padding: 8px;
  color: ${color.fixedWhite};

  div > div {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &:hover {
    background: ${MIDNIGHT_BLUE_TOKEN.COLOR.HOVER} !important;
  }
`;

const CheckContainer = styled.div`
  width: 14px;
  height: 14px;
`;

const Name = styled.span`
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Company: React.FC<
  { isCurrentCompany: boolean } & Pick<CompanyMetadata, 'companyId' | 'companyName'>
> = ({ isCurrentCompany, companyName, companyId }) => {
  const isMatchLiveDeliveries = Boolean(useMatchLiveDeliveries());
  const isMatchGeoLocationHistory = Boolean(useMatchGeoLocationHistory());
  const isMatchCourierDetails = Boolean(useMatchCourierDetails());
  const navigate = useNavigate();
  const { search } = useLocation();

  const [, setSearchParams] = useSearchParams();

  const handleOnClick = () => {
    if (isCurrentCompany) {
      return;
    }

    if (isMatchCourierDetails) {
      const newSearchParams = new URLSearchParams(search);
      newSearchParams.set('companyId', companyId);
      navigate({
        pathname: '/',
        search: newSearchParams.toString(),
      });
      return;
    }

    setSearchParams(prevSearchParams => {
      // Remove `courierId` if user navigates away from "Live deliveries"
      if (isMatchLiveDeliveries) {
        prevSearchParams.delete('courierId');
      }

      if (isMatchGeoLocationHistory) {
        prevSearchParams.delete('courierId');
        prevSearchParams.delete('date');
      }

      prevSearchParams.set('companyId', companyId);
      return prevSearchParams;
    });
  };

  return (
    <StyledMenuActionItemCompany $active={isCurrentCompany} variant="grey" onClick={handleOnClick}>
      <Name data-current={isCurrentCompany}>{companyName}</Name>
      {isCurrentCompany && (
        <CheckContainer>
          <Checkmark fill={color.fixedWhite} />
        </CheckContainer>
      )}
    </StyledMenuActionItemCompany>
  );
};

const ListOfCompany: React.FC<{ className?: string }> = ({ className }) => {
  const { user } = useAuthState();
  const { currentCompany } = useCurrentCompany();
  const companiesByUser = useCompaniesByUserId({ userId: user?.id });
  const sortedCompanies = companiesByUser?.data?.sort((a, b) =>
    a.companyName.localeCompare(b.companyName),
  );

  return (
    <StyledMenuItemGroup className={className} aria-label="companies">
      <ListContainer>
        {companiesByUser.isPending && <Skeleton $width="8rem" $height="calc(1.25rem - 4px)" />}
        {sortedCompanies?.map(({ companyId, companyName }) => (
          <Company
            key={companyId}
            companyId={companyId}
            companyName={companyName}
            isCurrentCompany={companyId === currentCompany.data?.id}
          />
        ))}
      </ListContainer>
    </StyledMenuItemGroup>
  );
};

export default ListOfCompany;
